import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydSwitch from '../../../../../../../commonDesign/SydSwitch'
import SydInput from '../../../../../../../commonDesign/SydInput'

const swatchStyle = (color) => ({
  width: 16,
  height: 16,
  borderRadius: 3,
  border: '1px solid var(--border-color)',
  backgroundColor: color || 'transparent',
  flexShrink: 0
})

const SEMANTIC_COLOR_VARS = {
  neutral: 'var(--color-neutral-500)',
  primary: 'var(--color-primary-500)',
  secondary: 'var(--color-secondary-500)',
  accent: 'var(--color-accent-500)'
}

function colorSwatchVar (value) {
  if (!value || value === 'none' || value === 'custom') return null
  if (SEMANTIC_COLOR_VARS[value]) return SEMANTIC_COLOR_VARS[value]
  return `var(--palette-${value}-500)`
}

function VariantParamControls ({ parameters, values, onChange }) {
  const merged = useMemo(() => {
    const defaults = Object.fromEntries(parameters.map(p => [p.key, p.default]))
    return { ...defaults, ...values }
  }, [parameters, values])

  const handleChange = useCallback((key, value) => {
    onChange({ ...merged, [key]: value })
  }, [merged, onChange])

  if (!parameters?.length) return null

  return (
    <>
      {parameters.map((param) => {
        if (param.type === 'boolean') {
          return (
            <Grid item xs={12} md={6} key={param.key}>
              <SydLabel label={param.label}>
                <SydSwitch
                  value={!!merged[param.key]}
                  onChange={(e, checked) => handleChange(param.key, checked)}
                />
              </SydLabel>
            </Grid>
          )
        }

        if (param.type === 'select' || param.type === 'color') {
          const selectOptions = (param.options || []).map(o => {
            const swatch = param.type === 'color' ? colorSwatchVar(o.value) : null
            return {
              value: o.value,
              label: swatch
                ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={swatchStyle(swatch)} />
                    {o.label}
                  </span>
                )
                : o.label
            }
          })

          const currentValue = merged[param.key] ?? param.default
          const isCustom = currentValue === 'custom'
          const customFields = param.customFields || []

          return (
            <React.Fragment key={param.key}>
              <Grid item xs={12} md={6}>
                <SydLabel label={param.label}>
                  <SydSelect
                    options={selectOptions}
                    value={currentValue}
                    onChange={(e) => handleChange(param.key, e.target.value)}
                  />
                </SydLabel>
              </Grid>
              {isCustom && customFields.length > 0
                ? customFields.map((field) => {
                  const fieldValue = merged[field.key] ?? field.default
                  return (
                    <Grid item xs={12} md={6} key={field.key}>
                      <SydLabel label={field.label}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={swatchStyle(fieldValue)} />
                          <SydInput
                            value={fieldValue}
                            onChange={(e) => handleChange(field.key, e.target.value)}
                            placeholder={field.default}
                          />
                        </div>
                      </SydLabel>
                    </Grid>
                  )
                })
                : null}
            </React.Fragment>
          )
        }

        return null
      })}
    </>
  )
}

VariantParamControls.propTypes = {
  parameters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    default: PropTypes.any,
    options: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired
    })),
    customFields: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      default: PropTypes.string
    }))
  })).isRequired,
  values: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

VariantParamControls.defaultProps = {
  values: {}
}

export default VariantParamControls
